import { RealtimeEventHandler } from './event_handler.js';
import { RealtimeUtils } from './utils.js';

/** GA server event types → beta names used by the vendored SDK handlers. */
const GA_TO_BETA_EVENT_TYPES = {
  // Conversation item lifecycle: GA renamed "created" → "added"
  'conversation.item.added': 'conversation.item.created',
  // Audio response events renamed in GA
  'response.output_audio.delta': 'response.audio.delta',
  'response.output_text.delta': 'response.text.delta',
  'response.output_audio_transcript.delta': 'response.audio_transcript.delta',
  'response.output_audio.done': 'response.audio.done',
  'response.output_text.done': 'response.text.done',
  'response.output_audio_transcript.done': 'response.audio_transcript.done',
};

const PREVIEW_MODEL_ALIASES = {
  'gpt-4o-realtime-preview': 'gpt-realtime-mini',
  'gpt-4o-realtime-preview-2025-06-03': 'gpt-realtime-mini',
  'gpt-4o-realtime-preview-2024-12-17': 'gpt-realtime-mini',
  'gpt-4o-mini-realtime-preview': 'gpt-realtime-mini',
};

function resolveModel(model) {
  return PREVIEW_MODEL_ALIASES[model] || model || 'gpt-realtime-mini';
}

function buildWebSocketSubprotocols(apiKey) {
  const subprotocols = ['realtime'];
  if (apiKey) {
    subprotocols.push(`openai-insecure-api-key.${apiKey}`);
  }
  return subprotocols;
}

export class RealtimeAPI extends RealtimeEventHandler {
  /**
   * Create a new RealtimeAPI instance
   * @param {{url?: string, apiKey?: string, dangerouslyAllowAPIKeyInBrowser?: boolean, debug?: boolean}} [settings]
   * @returns {RealtimeAPI}
   */
  constructor({ url, apiKey, dangerouslyAllowAPIKeyInBrowser, debug } = {}) {
    super();
    this.defaultUrl = 'wss://api.openai.com/v1/realtime';
    this.url = url || this.defaultUrl;
    this.apiKey = apiKey || null;
    this.debug = !!debug;
    this.ws = null;
    if (globalThis.document && this.apiKey) {
      if (!dangerouslyAllowAPIKeyInBrowser) {
        throw new Error(
          `Can not provide API key in the browser without "dangerouslyAllowAPIKeyInBrowser" set to true`,
        );
      }
    }
  }

  /**
   * Tells us whether or not the WebSocket is connected
   * @returns {boolean}
   */
  isConnected() {
    return !!this.ws;
  }

  /**
   * Writes WebSocket logs to console
   * @param  {...any} args
   * @returns {true}
   */
  log(...args) {
    const date = new Date().toISOString();
    const logs = [`[Websocket/${date}]`].concat(args).map((arg) => {
      if (typeof arg === 'object' && arg !== null) {
        return JSON.stringify(arg, null, 2);
      } else {
        return arg;
      }
    });
    if (this.debug) {
      console.log(...logs);
    }
    return true;
  }

  /**
   * Connects to Realtime API Websocket Server
   * @param {{model?: string}} [settings]
   * @returns {Promise<true>}
   */
  async connect({ model } = { model: 'gpt-realtime-mini' }) {
    model = resolveModel(model);
    if (!this.apiKey && this.url === this.defaultUrl) {
    }
    if (this.isConnected()) {
      throw new Error(`Already connected`);
    }
    if (globalThis.WebSocket) {
      /**
       * Web browser
       */
      if (globalThis.document && this.apiKey) {

      }
      const WebSocket = globalThis.WebSocket;
      const wsUrl = `${this.url}${model ? `?model=${model}` : ''}`;
      const ws = new WebSocket(wsUrl, buildWebSocketSubprotocols(this.apiKey));
      ws.addEventListener('message', (event) => {
        const message = JSON.parse(event.data);
        this.receive(message.type, message);
      });
      return new Promise((resolve, reject) => {
        const connectionErrorHandler = () => {
          this.disconnect(ws);
          reject(new Error(`Could not connect to "${this.url}"`));
        };
        ws.addEventListener('error', connectionErrorHandler);
        ws.addEventListener('open', () => {
          this.log(`Connected to "${this.url}"`);
          ws.removeEventListener('error', connectionErrorHandler);
          ws.addEventListener('error', () => {
            this.disconnect(ws);
            this.log(`Error, disconnected from "${this.url}"`);
            this.dispatch('close', { error: true });
          });
          ws.addEventListener('close', () => {
            this.disconnect(ws);
            this.log(`Disconnected from "${this.url}"`);
            this.dispatch('close', { error: false });
          });
          this.ws = ws;
          resolve(true);
        });
      });
    } else {
      /**
       * Node.js
       */
      const moduleName = 'ws';
      const wsModule = await import(/* webpackIgnore: true */ moduleName);
      const WebSocket = wsModule.default;
      const wsUrl = `${this.url}${model ? `?model=${model}` : ''}`;
      const ws = new WebSocket(wsUrl, [], {
        finishRequest: (request) => {
          request.setHeader('Authorization', `Bearer ${this.apiKey}`);
          request.end();
        },
      });
      ws.on('message', (data) => {
        const message = JSON.parse(data.toString());
        this.receive(message.type, message);
      });
      return new Promise((resolve, reject) => {
        const connectionErrorHandler = () => {
          this.disconnect(ws);
          reject(new Error(`Could not connect to "${this.url}"`));
        };
        ws.on('error', connectionErrorHandler);
        ws.on('open', () => {
          this.log(`Connected to "${this.url}"`);
          ws.removeListener('error', connectionErrorHandler);
          ws.on('error', () => {
            this.disconnect(ws);
            this.log(`Error, disconnected from "${this.url}"`);
            this.dispatch('close', { error: true });
          });
          ws.on('close', () => {
            this.disconnect(ws);
            this.log(`Disconnected from "${this.url}"`);
            this.dispatch('close', { error: false });
          });
          this.ws = ws;
          resolve(true);
        });
      });
    }
  }

  /**
   * Disconnects from Realtime API server
   * @param {WebSocket} [ws]
   * @returns {true}
   */
  disconnect(ws) {
    if (!ws || this.ws === ws) {
      this.ws && this.ws.close();
      this.ws = null;
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
      return true;
    }
  }

  /**
   * Receives an event from WebSocket and dispatches as "server.{eventName}" and "server.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  receive(eventName, event) {
    const normalizedName = GA_TO_BETA_EVENT_TYPES[eventName] ?? eventName;
    const normalizedEvent =
      normalizedName !== eventName ? { ...event, type: normalizedName } : event;
    this.log(`received:`, normalizedName, normalizedEvent);
    this.dispatch(`server.${normalizedName}`, normalizedEvent);
    this.dispatch('server.*', normalizedEvent);
    return true;
  }

  /**
   * Sends an event to WebSocket and dispatches as "client.{eventName}" and "client.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  send(eventName, data) {
    if (!this.isConnected()) {
      throw new Error(`RealtimeAPI is not connected`);
    }
    data = data || {};
    if (typeof data !== 'object') {
      throw new Error(`data must be an object`);
    }
    const event = {
      event_id: RealtimeUtils.generateId('evt_'),
      type: eventName,
      ...data,
    };
    this.dispatch(`client.${eventName}`, event);
    this.dispatch('client.*', event);
    this.log(`sent:`, eventName, event);
    this.ws.send(JSON.stringify(event));
    return true;
  }
}
